import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { LinkService } from '@/ui/_apiUrls';
import { authenticatedApi } from '@/api/config';
import { useUser } from '@/contexts/UserContext/UserContext';
import Button from '../Button';
import logo from '@/assets/image/logo.png';

interface DeleteLinkDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  link: Record<string, any> | null;
  onSuccess?: () => void;
}

export default function DeleteLinkDrawer({ isOpen, onClose, link, onSuccess }: DeleteLinkDrawerProps) {
  const queryClient = useQueryClient();
  const { updateUser } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutate: deleteLink } = LinkService.delete({
    onSuccess: () => {
      setIsDeleting(false);
      queryClient.invalidateQueries({ queryKey: ['link'] });

      if (onSuccess) onSuccess();

      setTimeout(() => {
        onClose();
      }, 300);
    },
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isDeleting) {
      handleClose();
    }
  };

  const handleDelete = async () => {
    if (!link) return;

    // Check if this is a Google Review link
    const isGoogleReview = link.type === 'google-review' || link.name === 'Google Review';

    if (isGoogleReview) {
      // Handle Google Review link deletion
      setIsDeleting(true);
      try {
        // Send request to clear the Google Review URL
        const response = await authenticatedApi.post('/api/v1/user/google-review-url/', {
          google_review_url: null,
        });

        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['user'] });
        queryClient.invalidateQueries({ queryKey: ['user-detail'] });

        // Update user context
        if (response.data?.result?.google_review_url === null) {
          updateUser({ google_review_url: null });
        }

        setIsDeleting(false);

        if (onSuccess) onSuccess();

        setTimeout(() => {
          onClose();
        }, 300);
      } catch (error) {

        setIsDeleting(false);
      }
    } else {
      // Handle regular link deletion
      setIsDeleting(true);
      deleteLink(link.id); // Changed from link._id to link.id to match backend response
    }
  };

  if (!link) return null;

  return (
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={handleBackdropClick}
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              duration: 0.5,
            }}
            className="fixed bottom-0 left-0 right-0 z-50 flex justify-center"
          >
            <div className="mx-0 flex max-h-[90vh] w-full max-w-[768px] flex-col overflow-hidden rounded-tl-[28px] rounded-tr-[28px] border border-zinc-300 bg-white shadow-lg sm:mx-4">
              {/* Handle */}
              <div className="flex w-full items-center justify-center px-4 pt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="h-1.5 w-[52px] cursor-pointer rounded-[20px] bg-zinc-300 transition-colors duration-200 hover:bg-zinc-400"
                  aria-label="Close drawer"
                />
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <div className="w-full space-y-6 py-4">
                  {/* Link Info */}
                  <div className="flex items-center gap-3 rounded-lg p-4 ">
                    <div className="w-[2.5rem] flex-shrink-0">
                      <img src={link?.thumbnail || logo} className="h-full w-[2.5rem] object-contain" alt="logo" />
                    </div>
                    <div className="ml-3 flex flex-1 flex-col gap-1 overflow-hidden">
                      <p
                        title={link?.name}
                        className="truncate break-words text-sm font-bold leading-[1.06rem] text-[#475467]"
                      >
                        {link?.name || ''}
                      </p>
                      <a
                        className="w-full truncate break-words text-xs text-[#667085]"
                        style={{ wordWrap: 'break-word' }}
                        title={link?.url || ''}
                        href={link?.url || '#'}
                      >
                        {link?.url || ''}
                      </a>
                    </div>
                  </div>

                  {/* Confirmation Message */}
                  <div>
                    <p className="text-[14px] font-medium text-[#333333]">Delete this link ?</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex w-full gap-3">
                    <Button
                      variant={isDeleting ? 'ghost' : 'secondary'}
                      className="align-center h-12 max-w-none flex-1 rounded-lg border-2 border-sky-500 bg-white !p-0 !px-5 !py-3 font-medium text-sky-500 transition-colors duration-200 hover:bg-sky-50"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      <p className="text-center font-roboto text-sm font-medium text-[#039BE5]">
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </p>
                    </Button>
                    <Button
                      variant="primary"
                      className="align-center h-12 max-w-none flex-1 rounded-lg bg-sky-500 !p-0 !px-5 !py-3 font-medium text-white transition-colors duration-200 hover:bg-sky-600"
                      onClick={handleClose}
                      disabled={isDeleting}
                    >
                      <p className="text-center font-roboto text-sm font-medium text-white">Cancel</p>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </>
  );
}
